import { useToastContext } from '@rtkwlf/fenrir-react';
import zipWith from 'lodash/zipWith';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useScannerDetails } from '@apis/rendall/queries/use-scanner-details/use-scanner-details';
import { CsvUpload } from '@packages/components/csv-upload/csv-upload';
import { validateIpAddresses } from '@packages/utils/validate-ip-addresses/validate-ip-addresses';

import type { ScanExclusionType } from '../../scan-exclusion/components/add-ip-range-form/add-ip-range-form';
import { PORT_REGEX } from '../ip-controls-config';

type Props = {
  ipControlsItemKey: string;
  ipControlsListKey: string;
  ipControlsList: Array<ScanExclusionType>;
  onFileUpload?: () => void;
};

const TOAST_DURATION = 10;

export const BulkImportIPRange = ({
  ipControlsItemKey,
  ipControlsListKey,
  ipControlsList,
  onFileUpload,
}: Props) => {
  const { addToast } = useToastContext();
  const { control, resetField } = useFormContext();
  const { append } = useFieldArray({
    control,
    name: ipControlsListKey,
  });
  const { t } = useTranslation('telemetryScannerConfig', {
    keyPrefix: 'notifications',
  });
  const { scannerId = '' } = useParams();
  const { data: hasPortExemptionCapability } = useScannerDetails(scannerId, {
    select: (data) => data.capabilities.includes('port_scan_exemption'),
  });

  const handleFileChange = (data: string[][]) => {
    if (data.length === 0 || data[0].length > 2) {
      addToast({
        title: t('error'),
        message: t('import.format'),
        appearance: 'danger',
        duration: TOAST_DURATION,
      });
      return;
    }

    const ipValidation = validateIpAddresses(
      data.map(([ipRange]) => ipRange),
      { existingEntries: ipControlsList.map(({ ipRange }) => ipRange) },
    );

    const ports = data.map((row) => row[1]);
    if (!hasPortExemptionCapability && ports.some((x) => x)) {
      addToast({
        title: t('error'),
        message: t('import.errorScannerOldVersion'),
        appearance: 'danger',
        duration: TOAST_DURATION,
      });
      return;
    }
    const portValidation = ports.every((port) =>
      port ? PORT_REGEX.test(port) : true,
    );

    if (ipValidation.invalid.size > 0 || !portValidation) {
      addToast({
        title: t('error'),
        message: t('import.invalid'),
        appearance: 'danger',
        duration: TOAST_DURATION,
      });
      return;
    }

    if (Object.keys(ipValidation.duplicate).length > 0) {
      addToast({
        title: t('error'),
        message: t('import.duplicate'),
        appearance: 'danger',
        duration: TOAST_DURATION,
      });
      return;
    }

    // callback on fileupload
    onFileUpload && onFileUpload();

    const importedEntries = zipWith(
      [...ipValidation.valid],
      ports,
      (ipRange, ports) => ({
        ipRange,
        ports,
      }),
    );

    append(importedEntries);

    resetField(`${ipControlsItemKey}`, {
      defaultValue: { ipRange: '', ports: '' },
      keepDirty: false,
    });

    addToast({
      title: t('success'),
      message: t('import.success'),
      appearance: 'success',
      duration: TOAST_DURATION,
    });
  };

  return <CsvUpload variant="secondary" onFileChange={handleFileChange} />;
};
